'use client'

import { useEffect, useState } from 'react'

// The platforms' logos, loaded once per page load and shared by every mark.
// useCatalog preloads them as soon as the catalog arrives, so by the time a
// list or a dropdown renders, the image is already in the browser cache and
// in `loaded`, and PlatformMark can show it at once; until then (and for a
// logo that fails, or a platform without one) the mark is the brand's
// initial capital, never an empty box.

const loaded = new Set<string>()
const failed = new Set<string>()
const pending = new Map<string, Promise<void>>()
const listeners = new Set<() => void>()

function notify() {
  for (const l of listeners) l()
}

export function preloadLogo(url: string | null | undefined): Promise<void> {
  if (!url || typeof window === 'undefined') return Promise.resolve()
  if (loaded.has(url) || failed.has(url)) return Promise.resolve()
  const inflight = pending.get(url)
  if (inflight) return inflight
  const p = new Promise<void>((resolve) => {
    const img = new Image()
    img.decoding = 'async'
    img.onload = () => { loaded.add(url); pending.delete(url); notify(); resolve() }
    img.onerror = () => { failed.add(url); pending.delete(url); notify(); resolve() }
    img.src = url
  })
  pending.set(url, p)
  return p
}

export function preloadLogos(urls: Array<string | null | undefined>) {
  for (const u of urls) void preloadLogo(u)
}

export type LogoState = 'loaded' | 'failed' | 'pending' | 'none'

export function logoState(url: string | null | undefined): LogoState {
  if (!url) return 'none'
  if (loaded.has(url)) return 'loaded'
  if (failed.has(url)) return 'failed'
  return 'pending'
}

/** The logo's state, kept current as it loads; starts the load if nobody has. */
export function useLogoState(url: string | null | undefined): LogoState {
  const [state, setState] = useState<LogoState>(() => logoState(url))
  useEffect(() => {
    setState(logoState(url))
    if (!url) return
    const onChange = () => setState(logoState(url))
    listeners.add(onChange)
    void preloadLogo(url)
    return () => { listeners.delete(onChange) }
  }, [url])
  return state
}
